import {Box, Button, Tooltip, Typography} from '@mui/material';
import {FC, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch} from 'src/app/hooks';
import {setActiveBusiness} from 'src/stores/common/commonSlice';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {useMissingBexio} from 'src/hooks/useMissingBexio';
import {imageWrapperSx, linkButtonSx} from './UserCell.sx';
import {UserCellType} from './UserCell.types';
import {blankUserPhoto, managerTypes} from '../../../utils/consts';
import {UserTypeEnum} from 'src/enums';

export const UserCell: FC<UserCellType> = ({data}) => {
    const intl = useIntl();
    // These describe the ROW, not the viewer. They used to read the row's role name; replacing that
    // with the viewer's persona made every row look like a business row to a business owner, so the
    // name column showed the business and every link led to the business page. userType is the row's
    // own answer to "who is this", which is what the role name was being used for here.
    const isBusiness = data.userType === UserTypeEnum.BUSINESS;
    const isCompany = data.userType === UserTypeEnum.COMPANY;
    // Red "no Bexio" flag applies only to drivers and companies (not business managers / other staff
    // shown in the Teams table), and never when a company (client) user is the one logged in - the
    // hook carries the viewer's side of that rule so no caller has to remember it.
    const showMissingBexio = useMissingBexio({
        hasBexio: !!data.bexioDataList?.length,
        userType: data.userType,
        isCompany: isCompany || 'name' in data,
    });
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const link = useMemo(() => {
        // Handle company case - company objects don't have roles but have user property
        if ('name' in data && (data as any).user?.id) {
            return AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String((data as any).user.id)]);
        }

        // Which page this row leads to follows from the kind of person it is.
        const isManager = managerTypes.some((type) => type === data?.userType);

        if (isBusiness) {
            return AppRouteSpace([
                AppRouteEnum.app,
                AppRouteEnum.users,
                AppRouteEnum.business,
                String(data.business.id),
            ]);
        }

        if (isManager) {
            return AppRouteSpace([AppRouteEnum.app, AppRouteEnum.technicalTeam, String(data.id)]);
        }

        return AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(data.id)]);
    }, [data.userType, data.id]);

    const handleLocate = () => {
        if (link) {
            // For companies, business info comes from the charger data passed to UserCell
            const businessId = data.business?.id;
            if (businessId) {
                dispatch(setActiveBusiness(businessId));
            }
            // No router state. This used to hand the next page a "page number" scraped off the
            // end of the list's query string, which stopped being a number the moment that query
            // carried filters - and the company page turned it into `page=NaN`. Back and the
            // breadcrumb both restore the list from the query itself.
            navigate(link);
        }
    };

    const userLabel = useMemo(() => {
        if ('name' in data) {
            return (data as any).name;
        }

        if (isCompany) {
            return data?.['company']?.['name'] ?? '';
        }

        if (!isBusiness) {
            const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
            return fullName || data.emailAddress || '';
        }

        return `${data.business.name}`;
    }, [data]);

    return (
        <Box>
            <Tooltip
                title={showMissingBexio ? intl.formatMessage({id: 'noBexioIntegration'}) : ''}
                arrow
            >
                <Button
                    onClick={handleLocate}
                    sx={linkButtonSx}
                >
                    <Box sx={imageWrapperSx}>
                        <img
                            src={data.avatar?.url || blankUserPhoto}
                            alt={`${data.firstName || ''} ${data.lastName || ''}`.trim() || data.emailAddress || ''}
                        />
                    </Box>
                    <Typography
                        variant="subtitle2"
                        color={showMissingBexio ? 'error.main' : 'primary.main'}
                    >
                        {userLabel}
                    </Typography>
                </Button>
            </Tooltip>
        </Box>
    );
};
